import {useMessages} from "i18n";
import {ModalSheet} from "components/ModalSheet";
import ReferFriendTag from "account/referral/ReferFriendTag";
import {campaignOfferFor} from "./landingOfferPlan";
import {type ReactNode, useMemo, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {RecipientGiftVisibility} from "./RecipientGiftVisibility";
import {useUser} from "UserProvider";
import {useLandingAnalytics} from "analytics/useLandingAnalytics";
import {useSiteNavigate} from "nav/SiteLink";
import {useSiteLinkOrigin} from "nav/useSiteLinkOrigin";
import {navigateSiteLink} from "nav/siteLinkOrigin";
import type {CampaignSurfaceId} from "analytics/landingVisit";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {CompactUpgradeDialog} from "upsell/CompactUpgradeDialog";
import {PlanChangeDialog} from "pricing/PlanChangeDialog";
import {planOf} from "home/gating";
import {EditorialColumn, FlowColumn, Hero, HeroLayout, LandingContent} from "home/HomePage.styles";
import {CampaignSurfaceRoot} from "./CampaignSurface.styles";
import {LandingCampaign} from "./LandingCampaign";
import {CampaignArtwork} from "./CampaignArtwork";
import {CampaignBrandStyle} from "./LandingCampaign.styles";
import {isHalloweenEdition, usesGoldBrand} from "./campaignPlayback";
import {arrivalSurface, campaignPress, PAGE_LOAD_EDITION} from "./halloweenEdition";
import {requestComposerTheme} from "home/personalization/composerThemeRequest";
import {chooseComposerFiles} from "./composerChooser";
import {useHalloweenArrival} from "./useHalloweenArrival";
import {useHalloweenCampaign} from "./useHalloweenCampaign";
import {useLandingCampaign} from "./useLandingCampaign";
import {useRecipientCampaignFit} from "./useRecipientCampaignFit";
import type {RecipientArcade} from "download/arcade/useRecipientArcade";
import {RecipientArcadeGame} from "download/arcade/RecipientArcadeGame";
import {GAME_SLOT_HEIGHTS} from "games/gameGeometry";
import {AuroraBackdrop, PageBackdrop} from "theme/backdrops";

/**
 * One playback, attribution and purchase flow; the caller owns its stable transfer-card column.
 *
 * `art` is the caller's OWN ground, and supplying it stands the campaign down
 * (kitecorp/transferify-ui#2391). The recipient page uses it for a look its sender PAID for — an
 * account page theme, or a wallpaper bought for this one transfer. The campaign cannot simply sit on
 * top: `ArtworkLayer` is an opaque `inset: 0` plate that would hide the picture outright, and the
 * editorial column is typeset in colours derived from the campaign's own scene, which guarantee
 * nothing against an arbitrary photograph. Standing down is also the same rule the backdrop layer has
 * always applied to paid placement — a page whose pixels are already sold once does not sell them
 * again (`download/pageBackdrop.ts`).
 */
export function CampaignSurface({surface, active = true, pending = false, composerOnly = false, art = null, arcade = null, children}: {
	surface: CampaignSurfaceId; active?: boolean; pending?: boolean; composerOnly?: boolean; art?: ReactNode;
	/**
	 * The recipient page's arcade (kitecorp/transferify-ui#2889). Only a page with the caller's own `art`
	 * offers its game, from the file card, and the game opens in this column over the art, never over the
	 * file card (kitecorp/transferify-ui#2990); closing it gives the art back.
	 */
	arcade?: RecipientArcade | null;
	children: ReactNode;
}) {
	const [user] = useUser();
	const messages = useMessages();
	const offerPlan = campaignOfferFor(planOf(user));
	const campaign = useMemo(() => messages.home.campaign.forOffer(offerPlan), [messages.home.campaign, offerPlan]);
	const readingRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const [giftVisible, setGiftVisible] = useState(false);
	const recipientFits = useRecipientCampaignFit(rootRef, surface === "recipient" && !pending, setGiftVisible);
	const gameOpen = arcade?.open ?? false;
	const showCampaign = !composerOnly && art === null && (surface === "landing" || (!pending && recipientFits));
	const campaignActive = active && !pending && showCampaign;
	const ownArt = !composerOnly && !showCampaign && art !== null;
	const openGame = gameOpen ? arcade?.game ?? null : null;
	// The api's switch (kitecorp/transferify-ui#3776), read once per page load; what it means for the rotation is halloweenEdition's rule.
	const halloween = useHalloweenCampaign();
	// A landing link that names an edition (`?tema=`) opens the rotation on it; which links count is halloweenEdition's rule.
	const {search} = useLocation();
	const playback = useLandingCampaign(campaignActive, readingRef, halloween, {surface, search}, PAGE_LOAD_EDITION);
	const {frame, paused, reduced} = playback;
	// A mail's landing link names where its reader saw the slide (`?surface=guest-email`, kitecorp/transferify-ui#3776), and
	// the attribution carries that rather than the page; which values count is `arrivalSurface`'s rule.
	const analytics = useLandingAnalytics(arrivalSurface(search, surface), offerPlan, {...playback, slide: frame.active, dissolving: frame.outgoing !== null, active: campaignActive});
	const visibleFrame = reduced ? {...frame, outgoing: null} : frame;
	const [pricingOpen, setPricingOpen] = useState(false);
	// No door of its own: the campaign offer's table is the only way in, and its checkout is credited from the
	// landing attribution the table hands the mint (`landing_purchase_links`), not by a client step.
	const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade(null);
	const navigate = useSiteNavigate();
	const siteOrigin = useSiteLinkOrigin();
	// The Halloween slide on the LANDING (kitecorp/transferify-ui#3776): the bat cursor and the green tile are its, not the recipient page's.
	const halloweenLanding = surface === "landing" && isHalloweenEdition(frame.active);
	useHalloweenArrival(surface === "landing" && campaignActive, rootRef, halloween);
	/*
	 * The press on a slide, its button or its artwork. An offer edition opens the plans. The Halloween slide sells
	 * nothing: on the landing it puts the edition's scene on the composer's draft and opens the composer's own file
	 * chooser, inside this same gesture because a browser opens one for nothing less; on the recipient page it brings
	 * the visitor to the landing's composer, leaving a claimed host the way the brand mark does. Which of the three it
	 * is is `campaignPress`'s rule.
	 */
	const pressCampaign = () => {
		const press = campaignPress(frame.active, surface);
		if (press.kind === "plans") { analytics.freezeExposure(); setPricingOpen(true); }
		else if (press.kind === "chooseFiles") { requestComposerTheme(press.template); chooseComposerFiles(rootRef.current); }
		else navigateSiteLink(siteOrigin, press.path, {push: href => navigate(href), assign: href => window.location.assign(href)});
	};
	/*
	 * The landing is the page's main landmark while it IS the page (kitecorp/transferify-ui#3016), so
	 * assistive tech and browsing agents can jump straight to the send form. A ROLE rather than a
	 * `<main>` element, because the element type must not follow `composerOnly`: the download page's
	 * reply keeps this same tree mounted, composer only, beside the recipient surface's own `<main>`,
	 * and changing the type on the way home would remount the composer and drop the sender's draft.
	 */
	const landmark = surface === "landing" && !composerOnly ? "main" : undefined;

	return (
		<CampaignSurfaceRoot ref={rootRef} as={surface === "recipient" ? "main" : "div"} role={landmark} $scene={frame.active}
			data-campaign-fit={showCampaign || undefined} data-own-art={ownArt || undefined}
			data-arcade-open={openGame !== null || undefined}
			style={openGame !== null ? {"--recipient-arcade-height": `${GAME_SLOT_HEIGHTS[openGame]}px`} as React.CSSProperties : undefined}
			data-campaign-surface={composerOnly ? undefined : surface} data-campaign-pending={pending || undefined} data-viewport-locked={surface === "recipient" ? "" : undefined}
			data-halloween={halloweenLanding || undefined}>
			{!composerOnly && (showCampaign ? <CampaignBrandStyle $scene={frame.active} $active={active} $gold={usesGoldBrand(frame.active)} $halloween={halloweenLanding}/>
				: <PageBackdrop><AuroraBackdrop/>{art}</PageBackdrop>)}
			{/* Keep the composer at one React position when returning home, without a second campaign. */}
			<LandingContent>
				{showCampaign && <CampaignArtwork campaign={campaign} readingRef={readingRef} active={active} frame={visibleFrame} paused={paused} onOpen={pressCampaign}/>}
				<Hero>
					<HeroLayout data-campaign-layout={composerOnly ? undefined : ""}>
						{(showCampaign || (ownArt && openGame !== null)) && <EditorialColumn>
							{gameOpen && arcade !== null ? <RecipientArcadeGame arcade={arcade}/>
								: <LandingCampaign campaign={campaign} offerPlan={offerPlan} surface={surface} assigned={playback.rotation !== null} visibleFraction={playback.ratio}
									active={active} frame={visibleFrame} paused={paused} readingRef={readingRef} onOpen={pressCampaign}/>}
						</EditorialColumn>}
						{surface === "recipient" ? <FlowColumn data-drop-container=""><RecipientGiftVisibility value={giftVisible}>{children}</RecipientGiftVisibility></FlowColumn> : children}
					</HeroLayout>
				</Hero>
			</LandingContent>
			{!composerOnly && <>
				<ModalSheet width={640} ariaLabel={messages.account.referral.title} onTransitionEntered={analytics.previewOpened} open={pricingOpen && offerPlan === "referral"} title={messages.account.referral.title} onClose={() => setPricingOpen(false)}>
					{pricingOpen && offerPlan === "referral" && <ReferFriendTag/>}
				</ModalSheet>
				{/* The campaign's offer and the checkout its press resolves are ONE paper (kitecorp/transferify-ui#3699):
					the offer stays up until the verdict arrives, so a checkout steps it in place rather than closing it
					and opening another. Closing the checkout closes the offer with it. */}
				<CompactUpgradeDialog
					checkout={checkout}
					onClose={() => { closeCheckout(); setPricingOpen(false); }}
					landingAttribution={analytics.held}
					campaign={offerPlan === "referral" ? undefined : {
						open: pricingOpen, onClose: () => setPricingOpen(false), offerPlan, currentPlanId: planOf(user),
						onTransitionEntered: analytics.previewOpened, onUpgrade,
					}}
				/>
				<PlanChangeDialog {...planChange}/>
			</>}
		</CampaignSurfaceRoot>
	);
}

